import { useContext, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { isEmpty } from 'lodash/fp';
import { UserContext, usePermissions } from 'providers/userContextProvider';
import { CustomField, FETCH_CUSTOM_FIELDS, modifyUpdatingCustomFields } from 'interfaces/customField';
import { Skeleton, Typography } from '../../../1-primative';
import { getEnabledCustomFields } from '../../workflowCompletion/subSteps/utils';
import { CustomFieldsDisplayVisual } from './customFieldsDisplay.visual';
import { PageObjectType } from '../../../5-page';
import {
  FETCH_USER_CUSTOM_FIELDS, FETCH_GOAL_CUSTOM_FIELDS, FETCH_ACCOUNT_CUSTOM_FIELDS,
  UPDATE_USER_CUSTOM_FIELDS, UPDATE_GOAL_CUSTOM_FIELDS, UPDATE_ACCOUNT_CUSTOM_FIELDS,
  UPDATE_CUSTODIAN_CUSTOM_FIELDS,
} from './customFieldsDisplay.queries';
import { Card, CardContent } from '../../../2-component';
import { translateBackend } from '../../../../assets/i18n/config';

export const CustomFieldsDisplay = ({
  objectId, objectType,
  options,
}: {
  objectId: string, objectType: PageObjectType
  options: any,
}) => {
  const { activeOrganization } = useContext(UserContext);
  const [derivedCustomFields, setDerivedCustomFields] = useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] = useState<string[]>();
  const { permissions } = usePermissions();
  const enabledCustomFields = getEnabledCustomFields(options);
  const { custodianConnection } = useContext(UserContext);
  const enableCustodianCustomFields = custodianConnection?.enableFetchCustodianCustomFields;
  const [objectData, setObjectData] = useState<any>();

  const customFieldsPermitted = permissions.includes('read:custom_fields') && enabledCustomFields.length !== 0;

  let FETCH_QUERY;
  let UPDATE_MUTATION;

  switch (objectType) {
    case PageObjectType.INDIVIDUAL:
    case PageObjectType.NON_INDIVIDUAL:
      FETCH_QUERY = FETCH_USER_CUSTOM_FIELDS(derivedCustomFieldsKeys, enableCustodianCustomFields);
      UPDATE_MUTATION = UPDATE_USER_CUSTOM_FIELDS;
      break;
    case PageObjectType.GOAL:
      FETCH_QUERY = FETCH_GOAL_CUSTOM_FIELDS(derivedCustomFieldsKeys, enableCustodianCustomFields);
      UPDATE_MUTATION = UPDATE_GOAL_CUSTOM_FIELDS;
      break;
    case PageObjectType.ACCOUNT:
      FETCH_QUERY = FETCH_ACCOUNT_CUSTOM_FIELDS(derivedCustomFieldsKeys, enableCustodianCustomFields, permissions);
      UPDATE_MUTATION = UPDATE_ACCOUNT_CUSTOM_FIELDS;
      break;
    default:
      throw new Error(`CustomFieldsDisplay: object type ${objectType} is not implemented`);
  }

  // loading 1 - custom fields definition
  const { loading: loading1 } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !customFieldsPermitted,
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map((a: any) => (a.key));
      setDerivedCustomFieldsKeys(customFieldKeys);
    },
  });

  // loading 2 - the actual values of custom fields
  const { loading: loading2 } = useQuery(FETCH_QUERY, {
    variables: { id: objectId },
    fetchPolicy: 'no-cache',
    skip: loading1,
    onCompleted: (data) => {
      const combinedCustomFields = data.fetch?.object?.customFields || [];
      if (data.fetch?.object?.custodianCustomFields) {
        combinedCustomFields.push(...(data.fetch?.object?.custodianCustomFields || []));
      }

      setObjectData({
        id: data.fetch?.object?.id,
        customFields: combinedCustomFields,
        ...data.fetch?.object,
      });
    },
  });

  const [updateCustodianCustomFieldsMutation, { loading: updateCustodianCustomFieldsLoading }] = useMutation(UPDATE_CUSTODIAN_CUSTOM_FIELDS);

  const [updateCustomFieldsMutation, { loading: updateUserLoading }] = useMutation(UPDATE_MUTATION, {
    refetchQueries: [FETCH_QUERY],
  });

  const submit = async () => {
    if (objectType === PageObjectType.INDIVIDUAL || objectType === PageObjectType.NON_INDIVIDUAL) {
      if (enableCustodianCustomFields) {
        await updateCustodianCustomFieldsMutation({
          variables: {
            input: {
              filter: {
                userId: objectId,
                custodianCustomFields: modifyUpdatingCustomFields(
                  objectData?.customFields,
                  derivedCustomFields ?? [],
                  derivedCustomFieldsKeys,
                ),
              },
            },
            skipErrorHandler: true,
          },
        });
      }
    }

    await updateCustomFieldsMutation({
      variables: {
        id: objectId,
        newCustomFields: enableCustodianCustomFields
          ? []
          : modifyUpdatingCustomFields(objectData?.customFields, derivedCustomFields ?? [], derivedCustomFieldsKeys),
      },
    });
  };

  if (!customFieldsPermitted) {
    return <></>;
  }

  return (
    <>
      <Typography variant='headingSmall' mb={2} data-testid='custom-fields-display-title'>
        {translateBackend(options.customTitle)}
      </Typography>
      <Card>
        <CardContent>
          {(loading1 || loading2) ? (
            <>
              <Skeleton width="200px" height="33px" variant="text" data-testid="custom-field-skeleton" />
              <Skeleton width="100px" height="19px" variant="text" data-testid="custom-field-skeleton" />
            </>
          ) : (
            <CustomFieldsDisplayVisual
              options={options}
              objectData={objectData}
              setObjectData={setObjectData}
              submit={submit}
              updating={updateUserLoading || updateCustodianCustomFieldsLoading}
              activeCustomFields={derivedCustomFieldsKeys}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default CustomFieldsDisplay;
